import React, { useState, useEffect, useCallback } from 'react';
import { authService, prizeService, pointsService } from '../services';
import BackButton from '../components/BackButton';
import BilibiliBinding from '../components/BilibiliBinding';
import BotActivation from '../components/BotActivation';
import MovieTicketIcon, { MovieTicketTextAmount } from '../components/MovieTicketIcon';
import './Profile.css';

const SOURCE_LABELS = {
  auto_bilibili: 'B站投喂折算',
  auto_bilibili_frozen: 'B站投喂折算（冻结）',
  manual_adjustment: '管理员调整',
  csv_import: 'CSV批量调整',
  redemption: '兑换扣分',
  redemption_refund: '兑换退款',
  frozen_merge: '冻结积分合并',
  frozen_migration: '自动积分转冻结'
};

const REASON_LABELS = {
  'Bilibili gift and SC settlement (frozen)': 'B站投喂折算（冻结）',
  'Bilibili gift and SC settlement': 'B站投喂折算',
  'Merge frozen points into available balance': '冻结积分合并为可用积分',
  'Point redemption': '积分兑换',
  'Point redemption refund': '积分兑换退款'
};

const POINT_TRANSACTION_LIMIT = 20;
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const DEFAULT_TRANSACTION_FILTERS = {
  source: 'all',
  direction: 'all',
  start_date: '',
  end_date: '',
  keyword: ''
};

function getDefaultTransactionFilters() {
  return { ...DEFAULT_TRANSACTION_FILTERS };
}

function formatNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number.toLocaleString() : '0';
}

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
}

function getPrizeImageUrl(url) {
  if (!url) return 'https://via.placeholder.com/120x120/9b59b6/ffffff?text=Prize';
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_ORIGIN}${url}`;
}

function formatRedemptionCost(redemption) {
  const currencyType = redemption.currency_type || 'points';
  const cost = redemption.currency_cost ?? redemption.points_cost ?? 0;
  return currencyType === 'movie_ticket' ? <MovieTicketTextAmount amount={formatNumber(cost)} /> : `${formatNumber(cost)} 积分`;
}

function deliveryLabel(type) {
  return type === 'virtual' ? '虚拟商品' : '实体商品';
}

function orderDeliveryLabel(order) {
  return Number(order?.has_physical || 0) ? '含实体商品' : '仅虚拟商品';
}

function statusLabel(status) {
  return {
    pending: '处理中',
    completed: '已完成',
    cancelled: '已取消',
    mixed: '部分处理',
    empty: '空订单'
  }[status] || status || '-';
}

function formatOrderTotal(order) {
  const parts = [];
  if (Number(order?.points_total || 0) > 0) parts.push(`${formatNumber(order.points_total)} 积分`);
  if (Number(order?.movie_ticket_total || 0) > 0) parts.push(`${formatNumber(order.movie_ticket_total)} 电影票`);
  return parts.length ? parts.join(' / ') : '0';
}

function buildOrderAddressText(order) {
  return [
    order?.recipient_name_snapshot,
    order?.phone_snapshot,
    `${order?.region_snapshot || ''}${order?.detail_address_snapshot || ''}`
  ].filter(Boolean).join(' ');
}

function parseMetadata(metadata) {
  if (!metadata) return {};
  if (typeof metadata === 'object') return metadata;

  try {
    return JSON.parse(metadata);
  } catch (error) {
    return {};
  }
}

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function getTransactionDisplay(tx) {
  const metadata = parseMetadata(tx.metadata);
  const delta = Number(tx.points_delta || 0);
  const isFrozenSettlement = tx.source === 'auto_bilibili_frozen';
  const hasFrozenBalance = hasOwn(metadata, 'frozen_balance_before')
    && hasOwn(metadata, 'frozen_balance_after');

  return {
    deltaText: `${isFrozenSettlement ? '冻结积分 ' : ''}${delta >= 0 ? '+' : ''}${formatNumber(delta)}`,
    balanceText: isFrozenSettlement && hasFrozenBalance
      ? `冻结 ${formatNumber(metadata.frozen_balance_before)} 到 ${formatNumber(metadata.frozen_balance_after)}`
      : `余额 ${formatNumber(tx.balance_before)} 到 ${formatNumber(tx.balance_after)}`
  };
}

function getTransactionAccountLabel(tx) {
  const parts = [];
  if (tx.bilibili_uname) parts.push(tx.bilibili_uname);
  if (tx.bilibili_uid) parts.push(`UID ${tx.bilibili_uid}`);
  return parts.join(' / ') || '-';
}

function formatTransactionReason(reason) {
  const text = typeof reason === 'string' ? reason.trim() : '';
  if (!text || text === '-') return '';
  return REASON_LABELS[text] || text;
}

function Profile() {
  const [user, setUser] = useState(null);
  const [redemptionOrders, setRedemptionOrders] = useState([]);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [orderDetails, setOrderDetails] = useState({});
  const [orderDetailLoadingId, setOrderDetailLoadingId] = useState(null);
  const [orderError, setOrderError] = useState('');
  const [pointsSummary, setPointsSummary] = useState(null);
  const [pointTransactions, setPointTransactions] = useState([]);
  const [pointHistoryExpanded, setPointHistoryExpanded] = useState(false);
  const [pointTxLoaded, setPointTxLoaded] = useState(false);
  const [pointTransactionsBound, setPointTransactionsBound] = useState(true);
  const [pointTxLoading, setPointTxLoading] = useState(false);
  const [pointTxError, setPointTxError] = useState('');
  const [pointTxPagination, setPointTxPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
    limit: POINT_TRANSACTION_LIMIT
  });
  const [pointTxFilters, setPointTxFilters] = useState(getDefaultTransactionFilters);
  const [pointTxDraftFilters, setPointTxDraftFilters] = useState(getDefaultTransactionFilters);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadPointTransactions = useCallback(async (page = 1, filters = getDefaultTransactionFilters()) => {
    try {
      setPointTxLoading(true);
      setPointTxError('');
      const data = await pointsService.getTransactions(page, POINT_TRANSACTION_LIMIT, filters);
      setPointTransactions(data.transactions || []);
      setPointTransactionsBound(data.bound !== false);
      setPointTxPagination(data.pagination || {
        page,
        totalPages: 1,
        total: 0,
        limit: POINT_TRANSACTION_LIMIT
      });
    } catch (err) {
      setPointTxError(err.response?.data?.message || '积分变动明细加载失败');
      setPointTransactions([]);
      setPointTxPagination({
        page,
        totalPages: 1,
        total: 0,
        limit: POINT_TRANSACTION_LIMIT
      });
    } finally {
      setPointTxLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
    loadRedemptionOrders();
    loadPointsSummary();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await authService.getProfile();
      setUser(data);
    } catch (err) {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadRedemptionOrders = async () => {
    try {
      setOrderError('');
      const data = await prizeService.getUserOrders();
      setRedemptionOrders(data);
    } catch (err) {
      setOrderError(err.response?.data?.message || '兑换订单加载失败');
      console.error('Failed to load redemption orders:', err);
    }
  };

  const loadPointsSummary = async () => {
    try {
      const data = await pointsService.getSummary();
      setPointsSummary(data);
    } catch (err) {
      console.error('Failed to load points summary:', err);
    }
  };

  const handlePointTxFilterChange = (field, value) => {
    setPointTxDraftFilters(prev => ({ ...prev, [field]: value }));
  };

  const handlePointTxFilterSubmit = async (event) => {
    event.preventDefault();
    const filters = { ...pointTxDraftFilters };
    setPointTxFilters(filters);
    await loadPointTransactions(1, filters);
  };

  const handlePointTxFilterReset = async () => {
    const filters = getDefaultTransactionFilters();
    setPointTxFilters(filters);
    setPointTxDraftFilters(filters);
    await loadPointTransactions(1, filters);
  };

  const loadPointTxPage = async (page) => {
    await loadPointTransactions(page, pointTxFilters);
  };

  const togglePointHistory = async () => {
    if (pointHistoryExpanded) {
      setPointHistoryExpanded(false);
      return;
    }

    setPointHistoryExpanded(true);
    if (!pointTxLoaded) {
      await loadPointTransactions(1, pointTxFilters);
      setPointTxLoaded(true);
    }
  };

  const toggleOrderDetail = async (orderId) => {
    if (expandedOrderId === orderId) {
      setExpandedOrderId(null);
      return;
    }

    setExpandedOrderId(orderId);
    if (orderDetails[orderId]) return;

    try {
      setOrderError('');
      setOrderDetailLoadingId(orderId);
      const data = await prizeService.getUserOrder(orderId);
      setOrderDetails(prev => ({ ...prev, [orderId]: data }));
    } catch (err) {
      setOrderError(err.response?.data?.message || '订单详情加载失败');
    } finally {
      setOrderDetailLoadingId(null);
    }
  };

  if (loading) {
    return <div className="loading">正在加载个人资料...</div>;
  }

  if (error || !user) {
    return <div className="container"><div className="form-error">{error || '未找到用户'}</div></div>;
  }

  const availablePoints = pointsSummary?.points ?? user.points ?? 0;
  const frozenPoints = pointsSummary?.frozen_points ?? user.frozen_points ?? 0;
  const totalPoints = pointsSummary?.total_points ?? user.total_points ?? (Number(availablePoints) + Number(frozenPoints));
  const movieTickets = pointsSummary?.movie_tickets ?? user.movie_tickets ?? 0;

  return (
    <div className="container">
      <BackButton to="/" />
      <h1 className="page-title">我的个人资料</h1>
      
      <div className="card" style={{ maxWidth: '800px', margin: '0 auto 2rem' }}>
        <h2 className="card-title" style={{ borderBottom: '2px solid var(--bg-purple-light)', paddingBottom: '1rem', marginBottom: '1.5rem' }}>用户信息</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
          <div style={{ padding: '1rem', backgroundColor: 'var(--bg-light)', borderRadius: '12px', border: '1px solid var(--bg-purple-light)' }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '0.5rem' }}>👤</span> 用户名
            </div>
            <div style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--text-dark)' }}>{user.username}</div>
          </div>
          
          <div style={{ padding: '1rem', backgroundColor: 'var(--bg-light)', borderRadius: '12px', border: '1px solid var(--bg-purple-light)' }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '0.5rem' }}>📧</span> 邮箱
            </div>
            <div style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--text-dark)' }}>{user.email}</div>
          </div>

          <div style={{ padding: '1rem', backgroundColor: 'var(--bg-light)', borderRadius: '12px', border: '1px solid var(--bg-purple-light)' }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '0.5rem' }}>⭐</span> 积分
            </div>
            <div style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--primary-purple)' }}>{availablePoints}</div>
            {Number(frozenPoints) > 0 && (
              <div style={{ marginTop: '0.35rem', fontSize: '0.85rem', color: 'var(--text-light)' }}>
                冻结 {frozenPoints}，总计 {totalPoints}
              </div>
            )}
          </div>

          <div style={{ padding: '1rem', backgroundColor: 'var(--bg-light)', borderRadius: '12px', border: '1px solid var(--bg-purple-light)' }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
              <MovieTicketIcon className="profile-movie-ticket-icon" /> 电影票
            </div>
            <div style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--primary-purple)' }}>
              <MovieTicketTextAmount amount={formatNumber(movieTickets)} />
            </div>
          </div>

          <div style={{ padding: '1rem', backgroundColor: 'var(--bg-light)', borderRadius: '12px', border: '1px solid var(--bg-purple-light)' }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '0.5rem' }}>🛡️</span> 角色
            </div>
            <div style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--text-dark)', textTransform: 'capitalize' }}>
              {user.role === 'admin' ? '管理员' : user.role === 'premium' ? '主播' : '普通用户'}
            </div>
          </div>
        </div>
        
        {/* B站账号绑定 */}
        <BilibiliBinding userId={user.id} />

        {pointsSummary && (
          <div className="profile-points-panel">
            <div className="profile-points-panel-header">
              <h3>积分来源</h3>
              <span>当前钱包概览</span>
            </div>

            <div className="profile-points-summary-grid">
              <div className="profile-point-stat profile-point-stat-primary">
                <span>可用积分</span>
                <strong>{formatNumber(pointsSummary.points)}</strong>
              </div>
              <div className="profile-point-stat profile-point-stat-primary">
                <span>电影票</span>
                <strong><MovieTicketTextAmount amount={formatNumber(pointsSummary.movie_tickets)} /></strong>
              </div>
              <div className="profile-point-stat">
                <span>冻结积分</span>
                <strong>{formatNumber(pointsSummary.frozen_points)}</strong>
              </div>
              <div className="profile-point-stat">
                <span>总积分</span>
                <strong>{formatNumber(pointsSummary.total_points)}</strong>
              </div>
              <div className="profile-point-stat">
                <span>投喂折算</span>
                <strong>{formatNumber(pointsSummary.auto_points)}</strong>
              </div>
              <div className={`profile-point-stat ${Number(pointsSummary.manual_points || 0) < 0 ? 'profile-point-stat-negative' : ''}`}>
                <span>手动调整</span>
                <strong>{formatNumber(pointsSummary.manual_points)}</strong>
              </div>
              <div className="profile-point-stat">
                <span>导入积分</span>
                <strong>{formatNumber(pointsSummary.import_points)}</strong>
              </div>
              <div className={`profile-point-stat ${Number(pointsSummary.redemption_spent || 0) !== 0 ? 'profile-point-stat-negative' : ''}`}>
                <span>兑换支出</span>
                <strong>{formatNumber(pointsSummary.redemption_spent)}</strong>
              </div>
              <div className="profile-point-stat">
                <span>待折算</span>
                <strong>{formatNumber(pointsSummary.remainder_battery)} 电池</strong>
              </div>
            </div>

            <div className="profile-points-history">
              <div className="profile-points-history-header">
                <div>
                  <h3>积分变动明细</h3>
                  <p>按时间倒序显示，每页 {POINT_TRANSACTION_LIMIT} 条；默认收起，展开后再加载。</p>
                </div>
                <div className="profile-points-history-actions">
                  <span>{pointTxLoaded ? `共 ${formatNumber(pointTxPagination.total)} 条` : '尚未加载'}</span>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={togglePointHistory}
                    disabled={pointTxLoading}
                  >
                    {pointHistoryExpanded ? '收起明细' : '展开明细'}
                  </button>
                </div>
              </div>

              {pointHistoryExpanded && (
                <>
              <form className="profile-point-filter-form" onSubmit={handlePointTxFilterSubmit}>
                <label>
                  开始日期
                  <input
                    type="date"
                    value={pointTxDraftFilters.start_date}
                    onChange={(event) => handlePointTxFilterChange('start_date', event.target.value)}
                  />
                </label>
                <label>
                  结束日期
                  <input
                    type="date"
                    value={pointTxDraftFilters.end_date}
                    onChange={(event) => handlePointTxFilterChange('end_date', event.target.value)}
                  />
                </label>
                <label>
                  来源
                  <select
                    value={pointTxDraftFilters.source}
                    onChange={(event) => handlePointTxFilterChange('source', event.target.value)}
                  >
                    <option value="all">全部来源</option>
                    {Object.entries(SOURCE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  方向
                  <select
                    value={pointTxDraftFilters.direction}
                    onChange={(event) => handlePointTxFilterChange('direction', event.target.value)}
                  >
                    <option value="all">全部</option>
                    <option value="gain">加分</option>
                    <option value="spend">扣分</option>
                  </select>
                </label>
                <label className="profile-point-filter-keyword">
                  关键词
                  <input
                    type="search"
                    value={pointTxDraftFilters.keyword}
                    onChange={(event) => handlePointTxFilterChange('keyword', event.target.value)}
                    placeholder="UID / 昵称 / 原因"
                  />
                </label>
                <div className="profile-point-filter-actions">
                  <button type="submit" className="btn btn-primary" disabled={pointTxLoading}>
                    筛选
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={handlePointTxFilterReset} disabled={pointTxLoading}>
                    清空
                  </button>
                </div>
              </form>

              {pointTxError && <div className="form-error profile-point-error">{pointTxError}</div>}

              {pointTxLoading ? (
                <div className="empty-state profile-point-empty">
                  <p>正在加载积分变动明细...</p>
                </div>
              ) : !pointTransactionsBound ? (
                <div className="empty-state profile-point-empty">
                  <p>绑定 B站账号后可查看积分变动明细</p>
                </div>
              ) : pointTransactions.length === 0 ? (
                <div className="empty-state profile-point-empty">
                  <p>暂无匹配的积分变动明细</p>
                </div>
              ) : (
                <>
                  <div className="profile-point-ledger">
                    {pointTransactions.map(tx => {
                      const display = getTransactionDisplay(tx);
                      const delta = Number(tx.points_delta || 0);
                      const reason = formatTransactionReason(tx.reason);

                      return (
                        <article className="profile-point-transaction" key={tx.id}>
                          <div className="profile-point-transaction-main">
                            <div>
                              <strong>{SOURCE_LABELS[tx.source] || tx.source}</strong>
                              <span>{formatDateTime(tx.created_at)}</span>
                            </div>
                            <div className={`profile-point-delta ${delta >= 0 ? 'gain' : 'spend'}`}>
                              {display.deltaText}
                            </div>
                          </div>
                          <div className="profile-point-transaction-account">
                            {getTransactionAccountLabel(tx)}
                          </div>
                          <div className="profile-point-transaction-meta">
                            <span>{display.balanceText}</span>
                            {reason && <span>{reason}</span>}
                          </div>
                        </article>
                      );
                    })}
                  </div>

                  <div className="profile-point-pagination">
                    <span>
                      第 {pointTxPagination.page || 1} / {pointTxPagination.totalPages || 1} 页
                    </span>
                    <div>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => loadPointTxPage((pointTxPagination.page || 1) - 1)}
                        disabled={pointTxLoading || (pointTxPagination.page || 1) <= 1}
                      >
                        上一页
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => loadPointTxPage((pointTxPagination.page || 1) + 1)}
                        disabled={pointTxLoading || (pointTxPagination.page || 1) >= (pointTxPagination.totalPages || 1)}
                      >
                        下一页
                      </button>
                    </div>
                  </div>
                </>
              )}
                </>
              )}
            </div>
          </div>
        )}
        
        {/* Bot激活 */}
        <BotActivation userId={user.id} />
      </div>

      <div className="profile-order-history">
        <div className="profile-order-history-header">
          <div>
            <h2>兑换历史</h2>
            <p>按订单显示兑换项目、备注和实体商品收货信息。</p>
          </div>
          <span>共 {formatNumber(redemptionOrders.length)} 个订单</span>
        </div>

        {orderError && <div className="form-error profile-order-error">{orderError}</div>}

        {redemptionOrders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🎁</div>
            <p>暂无兑换订单</p>
          </div>
        ) : (
          <div className="profile-order-list">
            {redemptionOrders.map((order) => {
              const expanded = expandedOrderId === order.id;
              const detail = orderDetails[order.id];
              const isLoadingDetail = orderDetailLoadingId === order.id;
              const addressText = buildOrderAddressText(detail || order);

              return (
                <article key={order.id} className={`profile-order-card ${expanded ? 'expanded' : ''}`}>
                  <button type="button" className="profile-order-summary" onClick={() => toggleOrderDetail(order.id)}>
                    <div className="profile-order-main">
                      <strong>订单 #{order.id}</strong>
                      <span>{formatDateTime(order.created_at)}</span>
                      {order.prize_names && <small>{order.prize_names}</small>}
                    </div>
                    <div className="profile-order-meta">
                      <span className={`profile-order-status ${order.status}`}>{statusLabel(order.status)}</span>
                      <span className={`profile-order-delivery ${Number(order.has_physical || 0) ? 'physical' : 'virtual'}`}>{orderDeliveryLabel(order)}</span>
                    </div>
                    <div className="profile-order-total">
                      <strong>{formatOrderTotal(order)}</strong>
                      <span>{order.line_count || 0} 个项目 · {order.item_count || 0} 件</span>
                    </div>
                    <span className="profile-order-toggle">{expanded ? '收起' : '查看详情'}</span>
                  </button>

                  {expanded && (
                    <div className="profile-order-detail">
                      {isLoadingDetail ? (
                        <div className="empty-state profile-order-empty">
                          <p>正在加载订单详情...</p>
                        </div>
                      ) : detail ? (
                        <>
                          <div className={`profile-order-address ${Number(detail.has_physical || 0) ? 'physical' : 'virtual'}`}>
                            <strong>{Number(detail.has_physical || 0) ? '收货地址' : '虚拟商品订单'}</strong>
                            <span>{Number(detail.has_physical || 0) ? (addressText || '这个订单没有地址快照') : '无需收货地址'}</span>
                          </div>

                          <div className="profile-order-items">
                            {(detail.items || []).map((item) => (
                              <div key={item.id} className="profile-order-item">
                                <img src={getPrizeImageUrl(item.prize_image)} alt={item.prize_name} />
                                <div>
                                  <strong>{item.prize_name}</strong>
                                  <span>{item.option_name || '默认规格'} · x{item.quantity || 1}</span>
                                  <span>{deliveryLabel(item.delivery_type)} · 使用 {formatRedemptionCost(item)}</span>
                                  {item.user_remark && <p>备注：{item.user_remark}</p>}
                                  {item.cancel_reason && <p>取消原因：{item.cancel_reason}</p>}
                                </div>
                                <span className={`profile-order-status ${item.status}`}>{statusLabel(item.status)}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="empty-state profile-order-empty">
                          <p>订单详情暂不可用</p>
                        </div>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Profile;
